import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Form, Button, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import Papa from "papaparse";
import { getSettingData } from "../../shared/scribe";
import ScribeContentModal from "../../components/scribe-content-modal";
import DeleteConfirmationModal from "../../components/delete-confirmation-modal";
import Breadcrumbs from "../../components/breadcrumbs";
import { isAdminRole } from "../../shared/utils";
import { useAccess } from "../../contexts/AccessContext";

const steps = [
  { number: 1, title: "Input topic" },
  { number: 2, title: "Configuration" },
  { number: 3, title: "Generate" },
];

const ScribeContentGenerate = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = useMemo(() => isAdminRole(), []);
  const { hasEdit, canSaveEdit } = useAccess();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, _setCurrentStep] = useState(searchParams.get("id") ? 3 : 1);
  const [topic, setTopic] = useState("");
  const [content, setContent] = useState("");
  const [listData, setListData] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [contentTypeId, setContentTypeId] = useState();
  const [contentType, setContentType] = useState();
  const [useSeo, setUseSeo] = useState(0);
  const [isQuestion, setIsQuestion] = useState(0);
  const [seoKeywords, setSeoKeywords] = useState("");
  const [wordsCount, setWordsCount] = useState(6000);
  const [questionsCount, setQuestionsCount] = useState(20);
  const [generationPrompt, setGenerationPrompt] = useState();
  const [validationPrompt, setValidationPrompt] = useState();
  const fileInputRef = useRef(null);
  const [fileType, setFileType] = useState(null);
  const [errors, setErrors] = useState({});
  const scribeContentsToolId = 3;

  const validateStep1 = () => {
    const newErrors = {};

    if (listData.length > 0) {
      const rowsWithoutTitle = [];
      listData.forEach((row, index) => {
        if (!row.title || row.title.trim().length === 0) {
          rowsWithoutTitle.push(index);
        }
      });

      if (rowsWithoutTitle.length > 0) {
        newErrors.invalidRows = rowsWithoutTitle;
        toast.error(`Topic is required`);
      }

      if (listData.length > 20) {
        newErrors.topic = "Maximum 20 rows allowed in the file";
        toast.error(`Maximum 20 rows allowed in the file`);
      }
    } else {
      if (!topic || topic.trim().length === 0) {
        newErrors.topic = "Topic is required";
        toast.error("Topic is required");
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      if (!newErrors.invalidRows) {
        // toast.error(Object.values(newErrors)[0]);
      }
      return false;
    }

    return true;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!contentType) {
      newErrors.contentType = "Please select the content type";
      toast.error("Please select the content type");
      setErrors(newErrors);
      return false;
    }
    if (!generationPrompt) {
      newErrors.generationPrompt = "Generation prompt is required";
      toast.error("Generation prompt is required");
    }
    if (isQuestion == 0 && useSeo == 0) {
      if (wordsCount < 200 || wordsCount > 6000) {
        newErrors.wordsCount = "Words count must be between 200 and 6000";
        toast.error("Words count must be between 200 and 6000.");
      }
    }

    if (isQuestion === 1) {
      if (questionsCount < 1 || questionsCount > 20) {
        newErrors.questionsCount = "Questions count must be between 1 and 20";
        toast.error("Questions count must be between 1 and 20.");
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // toast.error(Object.values(newErrors)[0]);
      return false;
    }

    return true;
  };

  const setCurrentStep = async (step) => {
    if (currentStep === 1) {
      if (!validateStep1()) return;
    }
    if (currentStep === 2) {
      if (!validateStep2()) return;

      setIsSubmitting(true);
      try {
        const payload = {
          content_type_id: contentTypeId,
          generation_prompt: generationPrompt,
          fullname: localStorage.getItem("fullname"),
          word_count: useSeo == 0 ? wordsCount : 0,
          question_count: isQuestion == 1 ? questionsCount : 0,
          topics: listData.length
            ? listData
            : [{ title: topic, keywords: seoKeywords, content: content }],
        };
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/scribe-bot`, payload);
        setSearchParams((params) => {
          params.set("id", res.data.data);
          return params;
        });
      } catch (err) {
        toast.error("Failed to generate content. Please try again later.");
        console.error("Error generating content:", err);
        return;
      } finally {
        setIsSubmitting(false);
      }
    }
    _setCurrentStep(step);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Always clear existing data and errors when uploading
    setListData([]);
    setErrors({});
    setEditMode([]);

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        if (fileType === "json") {
          const jsonContent = JSON.parse(e.target.result);
          if (Array.isArray(jsonContent) && jsonContent.length > 0) {
            setListData(jsonContent);
          } else {
            throw new Error("JSON file must contain an array of data");
          }

          if (jsonContent.length === 0) {
            throw new Error("JSON file cannot be empty");
          }

          // Check if JSON has correct format
          const hasFormatIssues = jsonContent.some(
            (row) =>
              row.hasOwnProperty("Title") ||
              row.hasOwnProperty("Content") ||
              row.hasOwnProperty("Keywords") ||
              Array.isArray(row.keywords || row.Keywords),
          );

          if (hasFormatIssues) {
            throw new Error("Format wrong. Use lowercase: title, content, keywords (as string)");
          }

          setListData(jsonContent);
        } else if (fileType === "csv") {
          const csvContent = Papa.parse(e.target.result, { header: true });
          const newData = csvContent.data.filter(
            (row) => !!row.title || !!row.content || !!row.keywords,
          );
          if (newData.length) {
            setListData(newData);
          } else {
            throw new Error("Invalid file");
          }
        } else {
          throw new Error("Invalid file");
        }

        toast.success(`File uploaded successfully`);
      } catch (error) {
        if (error.message === "Invalid file") {
          toast.error(
            "Error processing file. Please ensure the file format is correct and contains data.",
          );
        } else {
          toast.error(`${error.message}`);
        }
        console.error("Error processing file:", error);
        setListData([]);
      }
    };

    reader.readAsText(file);
  };

  const handleFileUpload = (type) => {
    setFileType(type);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  // Poll api to get status every 2 seconds
  const [responseList, setResponseList] = useState([]);
  useEffect(() => {
    let interval;
    const id = searchParams.get("id");
    if (
      currentStep === 3 &&
      (!responseList.length || responseList.some((r) => r.status !== "VALIDATION_COMPLETED")) &&
      id
    ) {
      let search = `ids=[${id}]`;
      interval = setInterval(async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/scribe-bot?${search}`);
          setResponseList(res.data.data);
        } catch (err) {
          console.error("Error fetching status:", err);
        }
      }, 2000);
    } else if (interval) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [currentStep, responseList]);

  const [downloadingType, setDownloadingType] = useState();
  const downloadItems = (type, id) => {
    setDownloadingType(type);
    const url = `${process.env.REACT_APP_API_URL}/scribe-bot`;
    const params = new URLSearchParams({
      ids: `[${id}]`,
      output_format: type,
    });
    axios
      .put(`${url}?${params.toString()}`, undefined, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then(({ data }) => {
        const fileName = data.data;
        const fileUrl = `https://svhs-scribe-ai.s3.us-east-2.amazonaws.com/${fileName}`;
        fetch(fileUrl, { method: "GET" })
          .then((response) => response.blob())
          .then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          });
      })
      .catch((error) => {
        console.error("Error downloading file:", error);
        toast.error("Error downloading file");
      })
      .finally(() => {
        setDownloadingType(false);
      });
  };

  const sampleCSVDownload = () => {
    const fileUrl = `https://svhs-scribe-ai.s3.us-east-2.amazonaws.com/scribe.csv`;
    fetch(fileUrl, { method: "GET" })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "scribe.csv");
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      });
  };

  const [viewId, setViewId] = useState();
  const selectedData = useMemo(
    () => responseList?.find((d) => d.id === viewId),
    [responseList, viewId],
  );

  const [retryingIds, setRetryingIds] = useState([]);
  const retry = (ids) => {
    setRetryingIds(ids);
    const url = `${process.env.REACT_APP_API_URL}/scribe-bot-publish`;
    const params = new URLSearchParams({ ids: `[${ids}]` });
    axios
      .get(`${url}?${params.toString()}`, undefined, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then(({ data }) => {})
      .catch((error) => {
        console.error("Error retrying:", error);
        toast.error("Error retrying");
      })
      .finally(() => {
        setRetryingIds((prev) => prev.filter((id) => !ids.includes(id)));
      });
  };

  // To edit imported list data
  const [editMode, setEditMode] = useState([]);
  const [deleteConfirmationIndex, setDeleteConfirmationIndex] = useState();
  const onDelete = (i) => {
    setEditMode((prev) => {
      const newEditMode = prev.filter((idx) => idx !== i);
      return newEditMode.map((idx) => (idx > i ? idx - 1 : idx));
    });
    setListData((prev) => prev.filter((_, idx) => idx !== i));
    setDeleteConfirmationIndex();
    toast.success("Deleted successfully");
  };
  const onSave = (i) => {
    setEditMode((prev) => prev.filter((idx) => idx !== i));
  };

  useEffect(() => {
    getSettingData()
      .then((data) => {
        setContentTypes(data);
      })
      .catch((e) => {
        console.error(e.message);
      });
  }, []);

  const reloadResponseData = () => {
    const id = searchParams.get("id");
    if (id) {
      let search = `ids=[${id}]`;
      axios
        .get(`${process.env.REACT_APP_API_URL}/scribe-bot?${search}`)
        .then((res) => {
          setResponseList(res.data.data);
        })
        .catch((err) => {
          console.error("Error fetching status:", err);
        });
    }
  };

  return (
    <>
      <Row className="mt-2 mt-4 mx-4">
        <Breadcrumbs />
        <p className="section-title mb-5">Generate</p>
        {steps.map((step) => (
          <Col key={step.number} className="text-center d-flex align-items-center gap-2">
            <div
              className={`rounded-circle d-inline-flex align-items-center justify-content-center`}
              style={{
                width: "2.5rem",
                height: "2.5rem",
                border: "2px solid",
                borderColor: step.number <= currentStep ? "#0d6efd" : "#dee2e6",
                color: step.number <= currentStep ? "#0d6efd" : "#6c757d",
              }}
            >
              {step.number}
            </div>
            <div className={step.number <= currentStep ? "text-primary" : "text-muted"}>
              {step.title}
            </div>
          </Col>
        ))}
      </Row>
      <div className="m-4">
        {currentStep === 1 && (
          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-2 justify-content-between">
              {!listData.length && (
                <Form.Group>
                  <Form.Control
                    type="text"
                    placeholder="Enter topic"
                    value={topic}
                    onChange={(e) => {
                      setTopic(e.target.value);
                      setErrors((prev) => ({ ...prev, topic: "" })); // clear error on change
                    }}
                    style={{ width: "fit-content", minWidth: "400px" }}
                    isInvalid={!!errors.topic}
                  />
                </Form.Group>
              )}
              <div className="d-flex gap-2">
                <div>
                  <Button
                    variant="secondary"
                    className="ms-auto"
                    onClick={() => sampleCSVDownload()}
                  >
                    Download Sample CSV File
                  </Button>
                </div>
                <div>
                  <Button variant="secondary" onClick={() => handleFileUpload("csv")}>
                    Upload CSV
                  </Button>
                </div>
                <div>
                  <Button variant="secondary" onClick={() => handleFileUpload("json")}>
                    Upload JSON
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept={fileType === "json" ? ".json" : ".csv"}
                    onChange={handleFileChange}
                  />
                </div>
              </div>
            </div>
            {!listData.length && (
              <Form.Group>
                <Form.Control
                  as="textarea"
                  rows={12}
                  placeholder="Enter content"
                  value={content}
                  onChange={(e) => {
                    setContent(e.target.value);
                    setErrors((prev) => ({ ...prev, content: "" }));
                  }}
                />
              </Form.Group>
            )}
            {!!listData.length && (
              <Table responsive className="scribe-contents-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Content</th>
                    <th>Keywords</th>
                    <th>Edit</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {listData?.map((row, i) => (
                    <tr key={i}>
                      <td>
                        {editMode.includes(i) ? (
                          <Form.Group>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={row.title}
                              isInvalid={errors.invalidRows?.includes(i)}
                              onChange={(e) => {
                                setListData((prev) => {
                                  const newData = [...prev];
                                  newData[i] = {
                                    ...newData[i],
                                    title: e.target.value,
                                  };
                                  return newData;
                                });
                                if (errors.invalidRows?.includes(i) && e.target.value.trim()) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    invalidRows: prev.invalidRows?.filter(
                                      (rowIndex) => rowIndex !== i,
                                    ),
                                  }));
                                }
                              }}
                            />
                            <Form.Control.Feedback type="invalid">
                              Topic is required
                            </Form.Control.Feedback>
                          </Form.Group>
                        ) : row.title ? (
                          <div className="p-2">{row.title}</div>
                        ) : (
                          <Form.Group>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={row.title}
                              isInvalid={errors.invalidRows?.includes(i)}
                              onClick={() => setEditMode((prev) => [...prev, i])}
                              onChange={(e) => {
                                setListData((prev) => {
                                  const newData = [...prev];
                                  newData[i] = {
                                    ...newData[i],
                                    title: e.target.value,
                                  };
                                  return newData;
                                });
                                if (errors.invalidRows?.includes(i) && e.target.value.trim()) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    invalidRows: prev.invalidRows?.filter(
                                      (rowIndex) => rowIndex !== i,
                                    ),
                                  }));
                                }
                              }}
                              placeholder="Enter topic"
                            />
                            <Form.Control.Feedback type="invalid">
                              Topic is required
                            </Form.Control.Feedback>
                          </Form.Group>
                        )}
                      </td>

                      <td>
                        {editMode.includes(i) ? (
                          <Form.Group>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={row.content}
                              onChange={(e) => {
                                setListData((prev) => {
                                  const newData = [...prev];
                                  newData[i] = {
                                    ...newData[i],
                                    content: e.target.value,
                                  };
                                  return newData;
                                });
                              }}
                            />
                          </Form.Group>
                        ) : (
                          row.content
                        )}
                      </td>
                      <td>
                        {editMode.includes(i) ? (
                          <Form.Group>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={row.keywords}
                              onChange={(e) => {
                                setListData((prev) => {
                                  const newData = [...prev];
                                  newData[i] = {
                                    ...newData[i],
                                    keywords: e.target.value,
                                  };
                                  return newData;
                                });
                              }}
                            />
                          </Form.Group>
                        ) : (
                          row.keywords
                        )}
                      </td>
                      <td>
                        {!editMode.includes(i) ? (
                          <img
                            style={{ cursor: "pointer" }}
                            src={"/images/edit.svg"}
                            alt="edit"
                            onClick={() => setEditMode((prev) => [...prev, i])}
                          />
                        ) : (
                          <img
                            style={{ cursor: "pointer" }}
                            src={"/images/save.svg"}
                            alt="save"
                            onClick={() => onSave(i)}
                          />
                        )}
                      </td>
                      <td>
                        <img
                          style={{ cursor: "pointer" }}
                          src={"/images/delete-grey.svg"}
                          alt="trash"
                          onClick={() => setDeleteConfirmationIndex(i)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <Row>
              <Button
                className="ms-auto"
                style={{ width: "fit-content" }}
                onClick={() => setCurrentStep(2)}
              >
                Next
              </Button>
            </Row>
          </div>
        )}
        {currentStep === 2 && (
          <div className="d-flex flex-column gap-2 text-start">
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Content type</Form.Label>
                  <Form.Select
                    value={contentType}
                    onChange={(e) => {
                      setContentType(e.target.value);
                      const filteredData = contentTypes.find(
                        (m) => m.content_type === e.target.value,
                      );
                      setGenerationPrompt(filteredData.default_generation_prompt);
                      setValidationPrompt(filteredData.default_validation_prompt);
                      setUseSeo(filteredData.use_seo);
                      setIsQuestion(filteredData.is_question);
                      setContentTypeId(filteredData.id);
                      setErrors((prev) => ({
                        ...prev,
                        contentType: "",
                        generationPrompt: "",
                      }));
                    }}
                    isInvalid={!!errors.contentType}
                  >
                    <option disabled={contentType}>-- Please choose an option --</option>
                    {contentTypes.map((m) => (
                      <option key={m.content_type} value={m.content_type}>
                        {m.content_type}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                {useSeo == 1 && listData.length == 0 && (
                  <Form.Group>
                    <Form.Label>SEO Keywords</Form.Label>
                    <Form.Control
                      value={seoKeywords}
                      onChange={(e) => setSeoKeywords(e.target.value)}
                    />
                  </Form.Group>
                )}
                {isQuestion == 0 && useSeo != 1 && (
                  <Form.Group>
                    <Form.Label>Words Count</Form.Label>
                    <Form.Control
                      value={wordsCount}
                      onChange={(e) => {
                        setWordsCount(e.target.value);
                        setErrors((prev) => ({ ...prev, wordsCount: "" }));
                      }}
                      type="number"
                    />
                  </Form.Group>
                )}
                {isQuestion == 1 && (
                  <Form.Group>
                    <Form.Label>Questions Count</Form.Label>
                    <Form.Control
                      value={questionsCount}
                      onChange={(e) => {
                        setQuestionsCount(e.target.value);
                        setErrors((prev) => ({ ...prev, questionsCount: "" }));
                      }}
                      type="number"
                    />
                  </Form.Group>
                )}
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Generation Prompt</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    placeholder="Type your prompt here..."
                    value={generationPrompt}
                    onChange={(e) => {
                      setGenerationPrompt(e.target.value);
                      setErrors((prev) => ({ ...prev, generationPrompt: "" }));
                    }}
                    isInvalid={!!errors.generationPrompt}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Button style={{ width: "fit-content" }} onClick={() => _setCurrentStep(1)}>
                Previous
              </Button>
              <Button
                className="ms-auto"
                style={{ width: "fit-content" }}
                onClick={() => setCurrentStep(3)}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Please wait" : "Next"}
              </Button>
            </Row>
          </div>
        )}
        {currentStep === 3 && responseList.length === 0 && (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: "200px" }}
          >
            <img src="/images/loader.svg" alt="loading" style={{ width: "40px", height: "40px" }} />
            <p className="mt-2 text-muted">Waiting for response...</p>
          </div>
        )}
        {currentStep === 3 && responseList.length === 1 && (
          <div className="d-flex flex-column gap-2">
            <div
              style={{
                border: "1px solid #bbb",
                borderRadius: "8px",
                padding: "6px 12px",
                textAlign: "left",
              }}
            >
              {useSeo || responseList[0].useSeo ? (
                <div className="d-flex gap-2 my-2">
                  <StatusIndicator
                    status={
                      responseList[0].status === "SEO_OPTIMISATION_INPROGRESS"
                        ? "inprogress"
                        : "completed"
                    }
                  />
                  Scraping SEO data for topic: {topic || responseList[0].title}
                </div>
              ) : (
                <div className="d-flex gap-2 my-2">
                  <StatusIndicator status="completed" />
                  Starting content generation for topic: {topic || responseList[0].title}
                </div>
              )}
              <div className="d-flex gap-2 my-2">
                <StatusIndicator
                  status={
                    responseList[0].status === "GENERATION_INPROGRESS"
                      ? "inprogress"
                      : responseList[0].status === "GENERATION_ERROR"
                      ? "error"
                      : ["NOT_STARTED", "SEO_OPTIMISATION_INPROGRESS"].includes(
                          responseList[0].status,
                        )
                      ? "pending"
                      : "completed"
                  }
                />
                Generating content
              </div>
              <div className="d-flex gap-2 my-2">
                <StatusIndicator
                  status={
                    responseList[0].status === "VALIDATION_INPROGRESS"
                      ? "inprogress"
                      : responseList[0].status === "VALIDATION_ERROR"
                      ? "error"
                      : [
                          "NOT_STARTED",
                          "GENERATION_INPROGRESS",
                          "GENERATION_ERROR",
                          "SEO_OPTIMISATION_INPROGRESS",
                        ].includes(responseList[0].status)
                      ? "pending"
                      : "completed"
                  }
                />
                Validating content
              </div>
            </div>
            {!!responseList[0].error && (
              <div
                style={{
                  border: "1px solid red",
                  color: "red",
                  borderRadius: "8px",
                  padding: "6px 12px",
                  textAlign: "left",
                }}
              >
                Something went wrong while generating content. Please try again later.
                {/* Error: {responseList[0].error} */}
              </div>
            )}
            {["GENERATION_ERROR", "VALIDATION_ERROR"].includes(responseList[0].status) && (
              <Button
                style={{
                  width: "fit-content",
                  marginBottom: ".5rem",
                }}
                onClick={() => retry([responseList[0].id])}
                disabled={!!retryingIds.length}
              >
                {retryingIds.length ? "Retrying..." : "Retry"}
              </Button>
            )}
            {responseList[0].status === "VALIDATION_COMPLETED" && (
              <Button
                style={{ width: "fit-content" }}
                onClick={() => setViewId(responseList[0].id)}
              >
                View Generated Content
              </Button>
            )}
          </div>
        )}
        {currentStep === 3 && responseList.length > 1 && (
          <div className="text-start">
            {responseList.some((row) =>
              ["GENERATION_ERROR", "VALIDATION_ERROR"].includes(row.status),
            ) && (
              <Button
                style={{
                  width: "fit-content",
                  marginBottom: ".5rem",
                }}
                onClick={() =>
                  retry(
                    responseList
                      .filter((row) =>
                        ["GENERATION_ERROR", "VALIDATION_ERROR"].includes(row.status),
                      )
                      .map((r) => r.id),
                  )
                }
                disabled={!!retryingIds.length}
              >
                {retryingIds.length ? "Retrying failed contents..." : "Retry failed contents"}
              </Button>
            )}
            <Table responsive className="scribe-contents-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>View</th>
                </tr>
              </thead>
              <tbody>
                {responseList?.map((row, i) => (
                  <tr key={i}>
                    <td>{row.title}</td>
                    <td>
                      <div className="d-flex gap-2 align-items-center" style={{ lineHeight: "1" }}>
                        {!!(useSeo || row.useSeo) &&
                          row.status === "SEO_OPTIMISATION_INPROGRESS" && (
                            <>
                              <StatusIndicator status={"inprogress"} />
                              Scraping SEO data
                            </>
                          )}
                        {row.status === "GENERATION_INPROGRESS" && (
                          <>
                            <StatusIndicator status={"inprogress"} />
                            Generating content
                          </>
                        )}
                        {row.status === "GENERATION_ERROR" && (
                          <>
                            <StatusIndicator status={"error"} />
                            Error generating content
                          </>
                        )}
                        {row.status === "VALIDATION_INPROGRESS" && (
                          <>
                            <StatusIndicator status={"inprogress"} />
                            Validating content
                          </>
                        )}
                        {row.status === "VALIDATION_ERROR" && (
                          <>
                            <StatusIndicator status={"error"} />
                            Error validating content
                          </>
                        )}
                        {row.status === "NOT_STARTED" && (
                          <>
                            <StatusIndicator status={"pending"} />
                            Not started
                          </>
                        )}
                        {row.status === "VALIDATION_COMPLETED" && (
                          <>
                            <StatusIndicator status={"completed"} />
                            Completed
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      {["GENERATION_ERROR", "VALIDATION_ERROR"].includes(row.status) ? (
                        retryingIds.includes(row.id) ? (
                          "Retrying"
                        ) : (
                          <a href="#" onClick={() => retry([row.id])}>
                            Retry
                          </a>
                        )
                      ) : (
                        <img
                          style={{ cursor: "pointer" }}
                          src="/images/eye.svg"
                          alt=""
                          onClick={() => setViewId(row.id)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        <ScribeContentModal
          show={!!viewId}
          onHide={() => setViewId()}
          data={selectedData}
          onDownload={(type) => {
            downloadItems(type, viewId);
          }}
          isDownloading={!!downloadingType}
          onContentSave={(editContent) =>
            setResponseList((list) =>
              list.map((item) =>
                item.id === viewId ? { ...item, generated_content: editContent } : item,
              ),
            )
          }
          dataLoad={reloadResponseData}
          isAdmin={isAdmin}
          hasEdit={hasEdit(scribeContentsToolId)}
          canSaveEdit={canSaveEdit(scribeContentsToolId)}
        />
        {deleteConfirmationIndex >= 0 && (
          <DeleteConfirmationModal
            loading={false}
            onClose={() => setDeleteConfirmationIndex()}
            onConfirm={() => onDelete(deleteConfirmationIndex)}
          />
        )}
      </div>
    </>
  );
};

const StatusIndicator = ({ status }) => {
  if (status === "completed") {
    return (
      <div style={{ width: "24px", textAlign: "center" }}>
        <img src="/images/tick.svg" style={{ width: "14px" }} />
      </div>
    );
  }

  if (status === "inprogress") {
    return (
      <div style={{ width: "24px", textAlign: "center" }}>
        <img src="/images/loader.svg" style={{ width: "20px" }} />
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div style={{ width: "24px", textAlign: "center" }}>
        <img src="/images/circle-dashed.svg" style={{ width: "16px", opacity: 0.4 }} />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div style={{ width: "24px", textAlign: "center", color: "red" }}>
        <img src="/images/x-red.svg" style={{ width: "20px", opacity: 0.8 }} />
      </div>
    );
  }
};

export default ScribeContentGenerate;
